import React, { createContext, useCallback, useEffect, useState, ReactNode } from 'react';
import { TenantData, TenantContextType } from 'types/tenant';
import TenantService from 'service/TenantService';

const TenantContext = createContext<TenantContextType | null>(null);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Carrega dados do tenant baseado no domínio
   */
  const loadTenant = useCallback(async (domain?: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const currentDomain = domain || TenantService.getCurrentDomain();
      let tenantData = await TenantService.getTenantByDomain(currentDomain);
      if (!tenantData || !TenantService.validateTenantData(tenantData)) {
        // Se não veio válido, tenta o default uma vez
        const defaultTenant = await TenantService.getTenantByDomain('default');
        if (defaultTenant && TenantService.validateTenantData(defaultTenant)) {
          setTenant(defaultTenant);
          TenantService.applyTenantStyles(defaultTenant);
          console.log('Tenant default carregado');
        } else {
          // Se nem o default for válido, usa fallback silenciosamente
          const fallbackDomain = domain || TenantService.getCurrentDomain();
          setTenant({
            id: 'fallback',
            name: 'Sendio Chatbot',
            domain: fallbackDomain,
            logo: '', // Logo vazio para forçar uso do LogoMain
            primaryColor: '#1976d2',
            companyName: 'Sendio Chatbot'
          });
        }
      } else {
        setTenant(tenantData);
        TenantService.applyTenantStyles(tenantData);
        console.log('Tenant carregado com sucesso:', tenantData.name);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Erro ao carregar configurações do cliente';
      setError(errorMessage);
      console.error('Erro ao carregar tenant:', err);
      if (err?.response?.status === 502) {
        window.location.replace('/maintenance/error/500');
        return;
      }
      // Se não for erro 502, tenta buscar o default
      try {
        const defaultTenant = await TenantService.getTenantByDomain('default');
        if (!defaultTenant || !TenantService.validateTenantData(defaultTenant)) {
          throw new Error('Tenant default inválido ou null');
        } else {
          setTenant(defaultTenant);
          TenantService.applyTenantStyles(defaultTenant);
          setError(null);
          console.log('Tenant default carregado após erro');
        }
      } catch (defaultError) {
        console.error('Erro ao carregar tenant padrão:', defaultError);
        const fallbackDomain = domain || TenantService.getCurrentDomain();
        setTenant({
          id: 'fallback',
          name: 'Sendio Chatbot',
          domain: fallbackDomain,
          logo: '', // Logo vazio para forçar uso do LogoMain
          primaryColor: '#3363FF',
          companyName: 'Sendio Chatbot'
        });
        setError(null);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshTenant = useCallback(async () => {
    if (tenant?.domain) {
      await loadTenant(tenant.domain);
    } else {
      await loadTenant();
    }
  }, [tenant?.domain, loadTenant]);

  useEffect(() => {
    loadTenant();
    return () => {
      TenantService.removeTenantStyles();
    };
  }, [loadTenant]);

  useEffect(() => {
    const handleDomainChange = () => {
      const currentDomain = TenantService.getCurrentDomain();
      if (tenant && tenant.domain !== currentDomain) {
        loadTenant(currentDomain);
      }
    };

    window.addEventListener('popstate', handleDomainChange);
    return () => {
      window.removeEventListener('popstate', handleDomainChange);
    };
  }, [tenant, loadTenant]);

  useEffect(() => {
    if (tenant) {
      TenantService.applyTenantStyles(tenant);
    }
  }, [tenant]);

  const contextValue: TenantContextType = {
    tenant,
    isLoading,
    error,
    loadTenant,
    refreshTenant
  };

  // Removido o redirect global para /maintenance/error/500. Só redireciona em erro 502 no catch do loadTenant.
  if (typeof window !== 'undefined' && window.location.pathname === '/maintenance/error/500') {
    return null;
  }

  return <TenantContext.Provider value={contextValue}>{children}</TenantContext.Provider>;
};

export default TenantContext;
